import { useState, useEffect, useCallback, useRef } from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { AsyncResult, CommerceList } from '@msdyn365-commerce/retail-proxy';
import { addLinesAsync, removeLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CommerceListsDataActions.g';
import { buildWishlistLine } from '@msdyn365-commerce-modules/retail-actions';
import { useProductEngagementTracker } from '../../integrations/relewise/hooks/useProductEngagementTracker';
import { useDatalayerService } from '../../integrations/datalayer/hooks/useDatalayerService';
import { useCustomerWishlist } from './use-customer-wishlist';
import {
    getCurrentCustomerWishlist,
    getOrCreateCustomerWishlist,
    publishCustomerWishlist,
    refreshCustomerWishlist
} from '../../actions/get-customer-wishlist';

const PENDING_WISHLIST_ITEM_KEY = 'pending-wishlist-item';

export interface WishlistItemArgs {
    concatenatedId: string | undefined;
    itemId: string | undefined;
    variantRecordId: number | undefined;
    relewiseVariantId: string | undefined;
    relewiseProductId: string | undefined;
    variantDisplayName: string | undefined;
    discount: number | undefined;
    brand: string | undefined;
    color: string | undefined;
    price: number | undefined;
    size: string | undefined;
}

interface UseWishlistResult {
    isProductInWishlist: (variantRecordId: number | undefined) => boolean;
    isLoading: boolean;
    addToWishlistError: boolean;
    toggleWishlist: (args: WishlistItemArgs) => Promise<void>;
    isWishlistReady: boolean;
    refreshWishlist: () => Promise<void>;
    isRefreshing: boolean;
    hasRefreshError: boolean;
}

export const useWishlist = (
    context: ICoreContext,
    location: string,
    wishlists: AsyncResult<CommerceList[]> | undefined
): UseWishlistResult => {
    const { wishlist, status } = useCustomerWishlist(wishlists);
    const [hasMounted, setHasMounted] = useState(false);
    const isWishlistReady = hasMounted && (!context.request.user.isAuthenticated || status === 'SUCCESS');
    const [isLoading, setIsLoading] = useState(false);
    const [addToWishlistError, setAddToWishlistError] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [hasRefreshError, setHasRefreshError] = useState(false);
    const isMutating = useRef(false);
    const { trackProductEngagement } = useProductEngagementTracker(context.request, context.telemetry);
    const datalayerService = useDatalayerService({ request: context.request, telemetry: context.telemetry });

    useEffect(() => {
        setHasMounted(true);
    }, []);

    const logWishlistError = useCallback(
        (message: string) => {
            const fullMessage = `[${location}] ${message}`;
            console.error(fullMessage);
            context.telemetry.error(fullMessage);
        },
        [context.telemetry, location]
    );

    const refreshWishlist = useCallback(async (): Promise<void> => {
        if (typeof window === 'undefined' || !context.request.user.isAuthenticated) {
            return;
        }

        setIsRefreshing(true);
        setHasRefreshError(false);
        try {
            await refreshCustomerWishlist(context.actionContext, wishlists);
        } catch (error) {
            setHasRefreshError(true);
            logWishlistError(`Failed to refresh wishlist: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsRefreshing(false);
        }
    }, [context.actionContext, context.request.user.isAuthenticated, wishlists, logWishlistError]);

    const isProductInWishlist = useCallback(
        (variantRecordId: number | undefined): boolean => {
            if (variantRecordId === undefined) {
                return false;
            }
            return wishlist?.CommerceListLines?.some(line => line.ProductId === variantRecordId) ?? false;
        },
        [wishlist]
    );

    const addToWishlist = useCallback(
        async (args: WishlistItemArgs) => {
            if (args.variantRecordId === undefined) {
                return;
            }
            const customerAccountNumber = context.request.user.customerAccountNumber;
            if (!customerAccountNumber) {
                return;
            }

            const currentWishlist = await getOrCreateCustomerWishlist(context.actionContext, wishlists);
            const wishlistId = currentWishlist.Id;

            const updatedList = await addLinesAsync({ callerContext: context.actionContext }, wishlistId, [
                buildWishlistLine(args.variantRecordId, customerAccountNumber, wishlistId)
            ]);

            publishCustomerWishlist(context.actionContext, updatedList);
            setAddToWishlistError(false);

            if (args.concatenatedId) {
                datalayerService.pushAddToWishlist({
                    item_id: args.concatenatedId,
                    item_name: args.variantDisplayName,
                    discount: args.discount,
                    style_id: args.itemId,
                    index: 0,
                    item_brand: args.brand,
                    item_variant: args.color,
                    price: args.price,
                    quantity: 1,
                    size: args.size
                });
                trackProductEngagement(args.relewiseProductId, args.relewiseVariantId, true);
            }
        },
        [context.request.user.customerAccountNumber, context.actionContext, wishlists, datalayerService, trackProductEngagement]
    );

    useEffect(() => {
        if (typeof window === 'undefined' || !context.request.user.isAuthenticated || !isWishlistReady || wishlists?.status !== 'SUCCESS') {
            return;
        }

        const raw = window.sessionStorage.getItem(PENDING_WISHLIST_ITEM_KEY);
        if (!raw) {
            return;
        }

        let pendingArgs: WishlistItemArgs;
        try {
            pendingArgs = JSON.parse(raw) as WishlistItemArgs;
            if (!pendingArgs || typeof pendingArgs.variantRecordId !== 'number' || !Number.isFinite(pendingArgs.variantRecordId)) {
                throw new Error('Invalid wishlist item');
            }
        } catch {
            logWishlistError(`Failed to add item to wishlist from sessionStorage: Could not parse JSON: ${raw}`);
            window.sessionStorage.removeItem(PENDING_WISHLIST_ITEM_KEY);
            return;
        }

        window.sessionStorage.removeItem(PENDING_WISHLIST_ITEM_KEY);
        if (getCurrentCustomerWishlist(wishlists)?.CommerceListLines?.some(line => line.ProductId === pendingArgs.variantRecordId)) {
            return;
        }

        addToWishlist(pendingArgs).catch(error => {
            setAddToWishlistError(true);
            logWishlistError(
                `Failed to add item to wishlist from sessionStorage: ${error instanceof Error ? error.message : String(error)}`
            );
        });
    }, [isWishlistReady, addToWishlist, wishlists, logWishlistError, context.request.user.isAuthenticated]);

    const removeFromWishlist = useCallback(
        async (args: WishlistItemArgs) => {
            const currentWishlist = getCurrentCustomerWishlist(wishlists);
            if (!currentWishlist || args.variantRecordId === undefined) {
                return;
            }

            const wishlistLine = currentWishlist.CommerceListLines?.find(line => line.ProductId === args.variantRecordId);
            if (!wishlistLine) {
                return;
            }

            const updatedList = await removeLinesAsync({ callerContext: context.actionContext }, currentWishlist.Id, [wishlistLine]);
            publishCustomerWishlist(context.actionContext, updatedList);
            trackProductEngagement(args.relewiseProductId, args.relewiseVariantId, false);
        },
        [context.actionContext, trackProductEngagement, wishlists]
    );

    const toggleWishlist = useCallback(
        async (args: WishlistItemArgs) => {
            if (typeof window === 'undefined' || isMutating.current || args.variantRecordId === undefined) {
                return;
            }

            if (!context.request.user.isAuthenticated) {
                if (context.request.user.signInUrl) {
                    window.sessionStorage.setItem(PENDING_WISHLIST_ITEM_KEY, JSON.stringify(args));
                    window.location.assign(context.request.user.signInUrl);
                }
                return;
            }
            if (wishlists?.status !== 'SUCCESS') {
                return;
            }

            isMutating.current = true;
            setIsLoading(true);
            const isInWishlist =
                getCurrentCustomerWishlist(wishlists)?.CommerceListLines?.some(line => line.ProductId === args.variantRecordId) ?? false;
            try {
                if (isInWishlist) {
                    await removeFromWishlist(args);
                } else {
                    await addToWishlist(args);
                }
            } catch (error) {
                // Only set the error when adding to wishlist
                const errorMessage = error instanceof Error ? error.message : String(error);
                if (!isInWishlist) {
                    setAddToWishlistError(true);
                    logWishlistError(`Failed to add item to wishlist: ${errorMessage}`);
                } else {
                    logWishlistError(`Failed to remove item from wishlist: ${errorMessage}`);
                }
            } finally {
                isMutating.current = false;
                setIsLoading(false);
            }
        },
        [wishlists, context, addToWishlist, removeFromWishlist, logWishlistError]
    );

    return {
        isProductInWishlist,
        isLoading,
        addToWishlistError,
        toggleWishlist,
        isWishlistReady,
        refreshWishlist,
        isRefreshing,
        hasRefreshError
    };
};
